import type { RawDomainSchemaProperty } from '../resolvers/types';

export type FileUploadSubmitSelection = 'first' | 'all' | 'last';

export interface RawFileUploadSubmitConfig {
  selection?: FileUploadSubmitSelection;
  arrayKey: string;
  deleteIdsKey?: string;
}

export interface RawFileUploadConfig {
  multiple?: boolean;
  maxFiles?: number | null;
  existingId?: string;
  existingName?: string;
  removedIds?: string;
  submit: RawFileUploadSubmitConfig;
}

/** Runtime config passed to RhfFileUpload (field names are flat / dot paths). */
export interface ResolvedFileUploadConfig {
  multiple: boolean;
  maxFiles: number | null;
  existingIdField?: string;
  existingNameField?: string;
  removedIdsField?: string;
  submit: {
    selection: FileUploadSubmitSelection;
    arrayKey: string;
    deleteIdsKey?: string;
  };
}

export interface FileUploadSubmitConfig {
  fieldName: string;
  existingIdField?: string;
  existingNameField?: string;
  removedIdsField?: string;
  submit: {
    selection: FileUploadSubmitSelection;
    arrayKey: string;
    deleteIdsKey?: string;
  };
  /** Edit mode: delete existing server doc when no replacement upload is staged. */
  deleteExistingWithoutUpload?: (formData: Record<string, unknown>) => boolean;
}

export function resolvePropertyPointer(pointer: string): string {
  return pointer.replace(/^#\/properties\//, '').replace(/\/properties\//g, '.');
}

export function resolveFileUploadConfig(
  prop: RawDomainSchemaProperty,
): ResolvedFileUploadConfig | undefined {
  const raw = prop.fileUpload as RawFileUploadConfig | undefined;
  if (!raw?.submit?.arrayKey) return undefined;

  return {
    multiple: raw.multiple !== false,
    maxFiles: raw.maxFiles ?? null,
    existingIdField: raw.existingId ? resolvePropertyPointer(raw.existingId) : undefined,
    existingNameField: raw.existingName ? resolvePropertyPointer(raw.existingName) : undefined,
    removedIdsField: raw.removedIds ? resolvePropertyPointer(raw.removedIds) : undefined,
    submit: {
      selection: raw.submit.selection ?? 'first',
      arrayKey: raw.submit.arrayKey,
      deleteIdsKey: raw.submit.deleteIdsKey,
    },
  };
}

export function prefixFileUploadConfig(
  config: ResolvedFileUploadConfig,
  namePrefix?: string,
): ResolvedFileUploadConfig {
  if (!namePrefix) return config;
  const prefix = (path?: string) => (path ? `${namePrefix}.${path}` : undefined);
  return {
    ...config,
    existingIdField: prefix(config.existingIdField),
    existingNameField: prefix(config.existingNameField),
    removedIdsField: prefix(config.removedIdsField),
  };
}

export function toFileUploadSubmitConfig(
  resolved: ResolvedFileUploadConfig,
  fieldName: string,
  extras?: Pick<FileUploadSubmitConfig, 'deleteExistingWithoutUpload'>,
): FileUploadSubmitConfig {
  return {
    fieldName,
    existingIdField: resolved.existingIdField,
    existingNameField: resolved.existingNameField,
    removedIdsField: resolved.removedIdsField,
    submit: resolved.submit,
    ...extras,
  };
}
